import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppPagination from '../../../components/AppPagination';
import AppActionMenu from '../../../components/AppActionMenu';
import ToolsButton from '../../../components/buttons/ToolsButton';
import { useSubjectFeesListScreen } from './hooks/useSubjectFeesListScreen';

export default function SubjectFeesListScreen() {
  const { state, actions } = useSubjectFeesListScreen();
  const navigate = useNavigate();

  const statusTabs = [
    { key: 'active', label: 'Active' },
    { key: 'archived', label: 'Archived' },
    { key: 'all', label: 'All' },
  ];

  const toolsOptions = [
    { key: 'download', label: 'Download Excel', icon: 'download' },
  ];

  const hasActiveFilters = Boolean(
    state.searchText || state.aYear || state.courseId || state.subjectId
  );

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Academics</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">Subjectwise Fees Registry</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Subjectwise Fees Registry
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Configure course and subject fee schedules across academic years.
          </p>
        </div>

        {/* Actions Cluster: Segmented Status Tabs (Utilizes global top navbar '+ Create New' button) */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.setViewMode(tab.key)}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                  state.viewMode === tab.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Row: Searchbar, Academic Year, Course & Subject Filters, and Tools Button */}
      <section className="flex flex-col gap-3 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-xs">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search text */}
          <label className="relative flex-1 min-w-[220px]">
            <span className="material-symbols-outlined pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-outline text-lg">
              search
            </span>
            <input
              type="text"
              value={state.searchText}
              onChange={(event) => actions.setSearchText(event.target.value)}
              placeholder="Search course, subject, or year..."
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 pl-10 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
            />
          </label>

          {/* Academic Year Filter */}
          <div className="w-36">
            <input
              type="text"
              value={state.aYear}
              onChange={(e) => actions.setAYear(e.target.value)}
              placeholder="Year (e.g. 2024-25)"
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2.5 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
            />
          </div>

          {/* Course Dropdown */}
          <div className="min-w-[180px]">
            <select
              value={state.courseId}
              onChange={(e) => actions.setCourseId(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2.5 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs cursor-pointer"
            >
              <option value="">All Courses</option>
              {state.courseOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Subject Dropdown */}
          <div className="min-w-[180px]">
            <select
              value={state.subjectId}
              onChange={(e) => actions.setSubjectId(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2.5 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs cursor-pointer"
            >
              <option value="">All Subjects</option>
              {state.subjectOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={actions.resetFilters}
              className="inline-flex items-center gap-1 shrink-0 rounded-xl bg-surface-container px-3.5 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">filter_alt_off</span>
              <span>Reset</span>
            </button>
          )}

          {/* Tools Dropdown Button */}
          <div className="ml-auto flex items-center gap-3 shrink-0">
            <ToolsButton
              tools={toolsOptions}
              onSelect={actions.handleToolSelect}
              disabled={state.isExporting}
              triggerClassName={`h-10 px-4 bg-surface-container-highest hover:bg-outline-variant/40 text-on-surface-variant font-bold rounded-xl text-xs flex items-center gap-2 cursor-pointer transition select-none shadow-2xs ${
                state.isExporting ? 'opacity-60 pointer-events-none' : ''
              }`}
            />
          </div>
        </div>
      </section>

      {/* Data Table */}
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/50 text-[11px] font-black uppercase tracking-[0.08em] text-on-surface-variant">
                <th className="py-3.5 px-4">Academic Year</th>
                <th className="py-3.5 px-4">Course</th>
                <th className="py-3.5 px-4">Subject</th>
                <th className="py-3.5 px-4 text-right">Admission Fees</th>
                <th className="py-3.5 px-4 text-right">Tuition Fees</th>
                <th className="py-3.5 px-4 text-right">Courseware Fees</th>
                <th className="py-3.5 px-4 text-right">Total Fees</th>
                <th className="py-3.5 px-4 text-center">Status</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15 text-xs text-on-surface font-medium">
              {state.isLoading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <span>Loading subject fee schedules...</span>
                    </div>
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-4xl text-outline-variant">
                        payments
                      </span>
                      <p className="font-semibold text-sm">No fee schedules found</p>
                      <p className="text-xs text-on-surface-variant/80">
                        {hasActiveFilters
                          ? 'Try changing your search text or filter criteria.'
                          : 'Use the top "+ Create New" button to add your first fee schedule.'}
                      </p>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={actions.resetFilters}
                          className="mt-2 inline-flex items-center gap-1 rounded-xl bg-surface-container-high px-4 py-2 text-xs font-bold text-primary hover:bg-surface-container-highest cursor-pointer"
                        >
                          <span className="material-symbols-outlined text-sm">filter_alt_off</span>
                          <span>Reset Filters</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                state.rows.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/masters/subject-fees/entry?id=${item.id}`)}
                    className="hover:bg-surface-container-low/40 transition-colors cursor-pointer group"
                  >
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center gap-1.5 font-bold text-on-surface">
                        <span className="material-symbols-outlined text-[16px] text-primary/70">
                          calendar_today
                        </span>
                        <span>{item.ayear}</span>
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-on-surface">
                      {item.course_name || `Course #${item.course_id}`}
                    </td>
                    <td className="py-3.5 px-4 text-on-surface-variant font-medium">
                      {item.subject_name || `Subject #${item.subject_id}`}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono text-on-surface">
                      {Number(item.fees_admission || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono text-on-surface">
                      {Number(item.fees_tuition || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono text-on-surface">
                      {Number(item.fees_courseware || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono font-bold text-primary">
                      {Number(item.fees_total || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                          item.status === 'Active'
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                            : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            item.status === 'Active' ? 'bg-emerald-500' : 'bg-amber-500'
                          }`}
                        />
                        {item.status}
                      </span>
                    </td>
                    <td
                      className="py-3.5 px-4 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <AppActionMenu
                        triggerIcon="more_vert"
                        triggerLabel=""
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                        items={[
                          {
                            key: 'edit',
                            label: 'Edit',
                            icon: 'edit',
                            onClick: () => navigate(`/masters/subject-fees/entry?id=${item.id}`),
                          },
                          {
                            key: 'archive',
                            label: item.status === 'Active' ? 'Archive' : 'Restore',
                            icon: item.status === 'Active' ? 'archive' : 'unarchive',
                            onClick: () => actions.handleArchive(item.id),
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            icon: 'delete',
                            danger: true,
                            onClick: () => actions.handleDelete(item.id),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing{' '}
            {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </div>
    </div>
  );
}
